import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { Minus, Monitor, Plus, RotateCcw, Smartphone } from 'lucide-react';
import {
  buildStorefrontMediaProxyUrl,
  dragWebsiteMediaFrame,
  nudgeWebsiteMediaFrame,
  websiteMediaDefaultFrame,
  websiteMediaFrame,
  websiteMediaFrameDevices,
  websiteMediaValueWithFraming,
  websiteTemplateThumbnailSrc,
  zoomWebsiteMediaFrame,
  WEBSITE_MEDIA_FRAME_LIMITS,
  type WebsiteFrameDevice,
  type WebsiteFrameDirection,
  type WebsiteInterfaceLocale,
  type WebsiteLayoutFamily,
  type WebsiteMediaFrameEntry,
  type WebsiteMediaFrameSize,
  type WebsiteMediaValue,
  type WebsiteRegionFieldState,
  type WebsiteSlotSchema,
} from '@dukanti/shared';
import { directionForLocale } from '@dukanti/shared/i18n';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { RegionLocalWork } from '../../hooks/use-region-editor';
import { useFramingDraft } from '../../hooks/use-framing-draft';
import { useWebsiteT } from '../../hooks/use-website-t';
import { authoredMedia } from '../../lib/slot-values';
import { FramingReplica, type FramingReplicaMedia } from './framing-replica';
import { framingPanelId, framingTabId } from './framing-ids';
import { FRAMING_FOCUS_RING } from './framing-focus-ring';
import { FramingTabs } from './framing-tabs';

const FRAMING = 'admin.websiteTemplates.media.framing';
/** The −/+ buttons and the `+`/`-` keys change the zoom by this much. */
export const FRAMING_ZOOM_STEP = 0.1;
/** The position is announced this long after the last change (a drag is many changes). */
export const FRAMING_ANNOUNCE_DELAY_MS = 400;

export interface MediaFramingEditorProps {
  readonly slot: WebsiteSlotSchema;
  readonly state: WebsiteRegionFieldState;
  readonly layoutFamily: WebsiteLayoutFamily;
  /** The store's language: its direction picks the frame and default (PD7). */
  readonly interfaceLocale: WebsiteInterfaceLocale;
  /** The canvas device toggle: the tab the crop opens on. */
  readonly device: WebsiteFrameDevice;
  readonly supabaseUrl: string;
  /** A file is on its way: the crop waits. */
  readonly disabled?: boolean;
  /**
   * UI/UX P2-3: the slot is for one device only (a hero's mobile or desktop
   * image, `websiteMediaSlotDevice`). Only that device's framing is shown,
   * labelled with the device; no tabs.
   */
  readonly slotDevice?: WebsiteFrameDevice | null;
  /** Apply: the field's new state (the media with its framing). */
  readonly onApply: (state: WebsiteRegionFieldState) => void;
  /** The crop holds unapplied work (`null`: none). */
  readonly onLocalWorkChange: (work: RegionLocalWork | null) => void;
}

/**
 * Epic #228 P6: the crop editor of a media field (PRD §8.3–§8.7). The true
 * frame of the slot, the picture dragged inside it (clamped so the frame
 * stays covered), zoom from cover to 4×, Reset, Desktop / Mobile tabs for a
 * slot whose frame differs by device, and keyboard and touch alternatives.
 *
 * Every change is local until **Apply**, which hands the media with its
 * framing to the region editor (RULE 0 → the auto-save queue). **Cancel**
 * (or Escape inside the editor) drops only the unapplied crop. The editor
 * never says "saved": the save status chip of the dialog does.
 *
 * Framing exists on authored media only: the template's own image is shown
 * in its frame, read-only.
 */
export function MediaFramingEditor(props: MediaFramingEditorProps): React.JSX.Element {
  const { slot, state, layoutFamily, interfaceLocale, device, supabaseUrl } = props;
  const { t } = useWebsiteT();
  const direction = directionForLocale(interfaceLocale);
  const entry = useMemo(
    () => websiteMediaFrame(layoutFamily, slot.id, slot.mediaAspect),
    [layoutFamily, slot.id, slot.mediaAspect],
  );
  const media = authoredMedia(state);

  if (media) {
    // A different file starts at its default framing (PD6): a new editor.
    return (
      <AuthoredFraming
        key={media.mediaAssetId}
        {...props}
        media={media}
        entry={entry}
        direction={direction}
      />
    );
  }

  const devices = props.slotDevice ? [props.slotDevice] : websiteMediaFrameDevices(entry);
  const shownDevice = devices.includes(device) ? device : (devices[0] ?? 'desktop');
  const geometry = entry[shownDevice];
  const templateSrc =
    state.mode === 'default' && slot.defaults.kind === 'template-media'
      ? websiteTemplateThumbnailSrc(slot.defaults.assetKey, supabaseUrl)
      : null;

  return (
    <div className="space-y-1.5" data-testid="media-framing-editor" data-mode="read-only">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>{t('admin.websiteTemplates.media.current')}</Label>
        {props.slotDevice ? <DeviceBadge device={props.slotDevice} /> : null}
      </div>
      <FramingReplica
        aspect={geometry.aspect[direction]}
        media={templateSrc ? { kind: 'image', src: templateSrc, posterSrc: null } : null}
        frame={websiteMediaDefaultFrame(geometry, direction)}
        alt={t('admin.websiteTemplates.media.current')}
        emptyLabel={t('admin.websiteTemplates.media.emptyPreview')}
      />
      {templateSrc ? (
        <p className="text-muted-foreground text-xs" data-testid="framing-default-hint">
          {t(`${FRAMING}.defaultHint`)}
        </p>
      ) : null}
    </div>
  );
}

interface AuthoredFramingProps extends MediaFramingEditorProps {
  readonly media: WebsiteMediaValue;
  readonly entry: WebsiteMediaFrameEntry;
  readonly direction: WebsiteFrameDirection;
}

function AuthoredFraming({
  media,
  entry,
  direction,
  device,
  supabaseUrl,
  disabled = false,
  slotDevice = null,
  onApply,
  onLocalWorkChange,
}: AuthoredFramingProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const id = useId();
  const element = media.kind === 'video' ? 'video' : 'image';
  const defaults = useMemo(
    () => ({
      desktop: websiteMediaDefaultFrame(entry.desktop, direction, element),
      mobile: websiteMediaDefaultFrame(entry.mobile, direction, element),
    }),
    [direction, element, entry],
  );
  const framing = useFramingDraft({
    committed: media.framing,
    entry,
    initialDevice: device,
    defaults,
    slotDevice,
  });
  const { update, dirty, frame } = framing;
  const geometry = entry[framing.tab];
  const tabs = framing.devices.length > 1;

  const replicaMedia = useMemo<FramingReplicaMedia>(() => {
    const src = buildStorefrontMediaProxyUrl(supabaseUrl, media.mediaAssetId);
    const posterSrc = media.posterAssetId
      ? buildStorefrontMediaProxyUrl(supabaseUrl, media.posterAssetId)
      : null;
    return media.kind === 'video'
      ? { kind: 'video', src, posterSrc }
      : { kind: media.kind, src, posterSrc: null };
  }, [media.kind, media.mediaAssetId, media.posterAssetId, supabaseUrl]);

  const boxRef = useRef<HTMLDivElement>(null);
  const naturalRef = useRef<WebsiteMediaFrameSize | null>(null);
  const focusFrame = useCallback((): void => {
    boxRef.current?.focus({ preventScroll: true });
  }, []);

  // ---- live announcement of the position (throttled) ----------------------
  const [announcement, setAnnouncement] = useState('');
  const frameRef = useRef(frame);
  frameRef.current = frame;
  const announceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const announceLater = useCallback((): void => {
    if (announceTimer.current !== null) clearTimeout(announceTimer.current);
    announceTimer.current = setTimeout(() => {
      announceTimer.current = null;
      const current = frameRef.current;
      setAnnouncement(
        t(`${FRAMING}.positionStatus`, {
          x: Math.round(current.x * 100),
          y: Math.round(current.y * 100),
          zoom: formatZoom(current.zoom),
        }),
      );
    }, FRAMING_ANNOUNCE_DELAY_MS);
  }, [t]);

  // ---- local work: the dialog asks before closing, Escape cancels ----------
  const cancel = useCallback((): void => {
    framing.cancel();
    focusFrame();
  }, [focusFrame, framing]);
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  const reportRef = useRef(onLocalWorkChange);
  reportRef.current = onLocalWorkChange;
  useEffect(() => {
    reportRef.current(dirty ? { cancel: () => cancelRef.current() } : null);
  }, [dirty]);
  useEffect(
    () => () => {
      reportRef.current(null);
    },
    [],
  );

  // ---- drag (mouse, pen and touch alike) ------------------------------------
  const dragRef = useRef<{ readonly pointerId: number; x: number; y: number } | null>(null);
  const pendingRef = useRef({ dx: 0, dy: 0 });
  const frameRequest = useRef<number | null>(null);
  const flushDrag = useCallback((): void => {
    frameRequest.current = null;
    const { dx, dy } = pendingRef.current;
    pendingRef.current = { dx: 0, dy: 0 };
    const box = boxRef.current;
    const natural = naturalRef.current;
    if ((dx === 0 && dy === 0) || !box || !natural) return;
    const rect = box.getBoundingClientRect();
    update((current) =>
      dragWebsiteMediaFrame(
        current,
        { dx, dy },
        { width: rect.width, height: rect.height },
        natural,
      ),
    );
    announceLater();
  }, [announceLater, update]);

  useEffect(
    () => () => {
      if (frameRequest.current !== null) cancelAnimationFrame(frameRequest.current);
      if (announceTimer.current !== null) clearTimeout(announceTimer.current);
    },
    [],
  );

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (disabled || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    focusFrame();
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    pendingRef.current = {
      dx: pendingRef.current.dx + event.clientX - drag.x,
      dy: pendingRef.current.dy + event.clientY - drag.y,
    };
    drag.x = event.clientX;
    drag.y = event.clientY;
    if (frameRequest.current === null) frameRequest.current = requestAnimationFrame(flushDrag);
  };
  const endDrag = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (frameRequest.current !== null) cancelAnimationFrame(frameRequest.current);
    flushDrag();
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  // ---- keyboard and zoom ------------------------------------------------------
  const setZoom = (zoom: number): void => {
    update((current) => zoomWebsiteMediaFrame(current, zoom));
    announceLater();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const step = event.shiftKey ? 0.05 : 0.01;
    const arrow = ARROWS[event.key];
    if (arrow) {
      update((current) => nudgeWebsiteMediaFrame(current, arrow, step));
      announceLater();
    } else if (event.key === '+' || event.key === '=') {
      setZoom(frameRef.current.zoom + FRAMING_ZOOM_STEP);
    } else if (event.key === '-' || event.key === '_') {
      setZoom(frameRef.current.zoom - FRAMING_ZOOM_STEP);
    } else {
      return;
    }
    event.preventDefault();
  };

  const apply = (): void => {
    onApply({
      mode: 'authored',
      value: { v: websiteMediaValueWithFraming(media, framing.takeApplied()) },
    });
    focusFrame();
  };
  const reset = (): void => {
    framing.reset();
    focusFrame();
  };

  const [zoomMin, zoomMax] = WEBSITE_MEDIA_FRAME_LIMITS.zoom;
  const titleId = `${id}-title`;
  const hintId = `${id}-hint`;
  const panelProps = tabs
    ? {
        role: 'tabpanel',
        id: framingPanelId(id),
        'aria-labelledby': framingTabId(id, framing.tab),
      }
    : {};

  return (
    <section
      aria-labelledby={slotDevice ? `${titleId} ${id}-device` : titleId}
      className="space-y-3"
      data-testid="media-framing-editor"
      data-mode="edit"
      data-dirty={dirty}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={titleId} className="text-foreground text-sm font-medium">
          {t(`${FRAMING}.title`)}
        </h3>
        {slotDevice ? <DeviceBadge device={slotDevice} id={`${id}-device`} /> : null}
        {tabs ? (
          <FramingTabs
            devices={framing.devices}
            value={framing.tab}
            onChange={framing.setTab}
            idPrefix={id}
            label={t(`${FRAMING}.title`)}
          />
        ) : null}
      </div>

      <div
        {...panelProps}
        className="space-y-3"
        data-testid="framing-panel"
        data-device={framing.tab}
      >
        <FramingReplica
          aspect={geometry.aspect[direction]}
          media={replicaMedia}
          frame={frame}
          alt=""
          emptyLabel={t('admin.websiteTemplates.media.emptyPreview')}
          boxRef={boxRef}
          onNaturalSizeChange={(size) => {
            naturalRef.current = size;
          }}
          boxProps={{
            role: 'group',
            tabIndex: disabled ? -1 : 0,
            'aria-label': t(`${FRAMING}.position`),
            'aria-describedby': hintId,
            'aria-disabled': disabled || undefined,
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onKeyDown,
            className: cn(
              'cursor-grab touch-none outline-none active:cursor-grabbing aria-disabled:cursor-default',
              FRAMING_FOCUS_RING,
            ),
          }}
        />

        <div className="space-y-1">
          <p id={hintId} className="text-muted-foreground text-xs">
            {t(`${FRAMING}.dragHint`)}
          </p>
          {geometry.approximate ? (
            <p className="text-muted-foreground text-xs" data-testid="framing-preview-note">
              {t(`${FRAMING}.previewNote`)}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn('size-9 shrink-0', FRAMING_FOCUS_RING)}
            aria-label={t(`${FRAMING}.zoomOut`)}
            disabled={disabled || frame.zoom <= zoomMin}
            onClick={() => setZoom(frame.zoom - FRAMING_ZOOM_STEP)}
          >
            <Minus aria-hidden="true" className="size-4" />
          </Button>
          <input
            type="range"
            min={zoomMin}
            max={zoomMax}
            step={0.01}
            value={frame.zoom}
            disabled={disabled}
            aria-label={t(`${FRAMING}.zoom`)}
            aria-valuetext={`${formatZoom(frame.zoom)}×`}
            data-testid="framing-zoom"
            className={cn(
              'accent-admin-lime h-9 min-w-0 flex-1 cursor-pointer rounded-md outline-none disabled:cursor-default',
              FRAMING_FOCUS_RING,
            )}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn('size-9 shrink-0', FRAMING_FOCUS_RING)}
            aria-label={t(`${FRAMING}.zoomIn`)}
            disabled={disabled || frame.zoom >= zoomMax}
            onClick={() => setZoom(frame.zoom + FRAMING_ZOOM_STEP)}
          >
            <Plus aria-hidden="true" className="size-4" />
          </Button>
          <span
            aria-hidden="true"
            className="text-muted-foreground w-10 shrink-0 text-end text-xs tabular-nums"
          >
            {formatZoom(frame.zoom)}×
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="framing-reset"
            className={FRAMING_FOCUS_RING}
            disabled={disabled || framing.isDefault}
            onClick={reset}
          >
            <RotateCcw aria-hidden="true" className="size-4" />
            {t(`${FRAMING}.reset`)}
          </Button>
          {dirty ? (
            <div className="ms-auto flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="framing-cancel"
                className={FRAMING_FOCUS_RING}
                onClick={cancel}
              >
                {t('admin.websiteTemplates.common.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                data-testid="framing-apply"
                className={FRAMING_FOCUS_RING}
                disabled={disabled}
                onClick={apply}
              >
                {t(`${FRAMING}.apply`)}
              </Button>
            </div>
          ) : null}
        </div>

        <p className="sr-only" aria-live="polite" data-testid="framing-announcement">
          {announcement}
        </p>
      </div>
    </section>
  );
}

/** The one device a device-specific slot is for, named next to the title. */
function DeviceBadge({
  device,
  id,
}: {
  readonly device: WebsiteFrameDevice;
  readonly id?: string;
}): React.JSX.Element {
  const { t } = useWebsiteT();
  const Icon = device === 'desktop' ? Monitor : Smartphone;
  return (
    <span
      id={id}
      data-testid="framing-device"
      data-device={device}
      className="border-input text-foreground inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium"
    >
      <Icon aria-hidden="true" className="size-4" />
      {t(`${FRAMING}.${device}`)}
    </span>
  );
}

const ARROWS: Readonly<Record<string, 'left' | 'right' | 'up' | 'down'>> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
};

/** `1.5`, `2`, `1.25`: the zoom as people read it. */
function formatZoom(zoom: number): string {
  return String(Number(zoom.toFixed(2)));
}
